import { Navigate, Outlet, useLocation } from 'react-router';
import type { Permission } from '@taskflow/shared';
import { EmptyState, Spinner } from '@taskflow/ui';
import { ShieldOff } from 'lucide-react';
import { useAuth } from './AuthContext';
import { usePermissions } from './usePermissions';

/**
 * Route guard.
 *
 * Three states, and the middle one matters: while the session is still being
 * resolved we must render neither the app nor a redirect. Redirecting during
 * `loading` would bounce every signed-in user to /login on a hard refresh, then
 * bounce them back — a visible flicker that looks broken and loses their place.
 */
export function RequireAuth() {
  const { status } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-surface-base"
        aria-busy="true"
      >
        <Spinner size="lg" label="Checking your session" />
      </div>
    );
  }

  if (status === 'anonymous') {
    // The intended destination travels in the query string so sign-in returns the
    // user to where they were going, not to a generic landing page.
    const redirectTo = `${location.pathname}${location.search}`;
    const target =
      redirectTo === '/' || redirectTo === '/boards'
        ? '/login'
        : `/login?redirectTo=${encodeURIComponent(redirectTo)}`;

    return <Navigate to={target} replace />;
  }

  return <Outlet />;
}

/**
 * Guards a route on a permission.
 *
 * Renders a plain refusal rather than redirecting: the user IS signed in, so a
 * bounce to /login would be a lie about what went wrong. It also avoids the loop
 * where the guard redirects, the login page sees a session, and sends them straight
 * back.
 */
export function RequirePermission({ permission }: { permission: Permission }) {
  const { can } = usePermissions();

  if (!can(permission)) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-border-subtle bg-surface-raised">
          <EmptyState
            icon={<ShieldOff aria-hidden="true" />}
            title="You do not have access to this page"
            description="Your role does not include this area. If you think that is wrong, ask an administrator."
          />
        </div>
      </div>
    );
  }

  return <Outlet />;
}
